import { stepDotClass, stagePillClass } from "./mockupStyles";
import { Check } from "lucide-react";

export function ScreenshotsSection() {
  return (
    <section className="py-20 md:py-28 bg-navy-band border-y border-navy-100">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <p className="overline mb-3">Inside LeaseFlow</p>
          <h2 className="h-display text-3xl md:text-5xl text-navy-900">
            Built so your clients <span className="text-emerald-600">finally get it.</span>
          </h2>
          <p className="mt-5 text-lg text-navy-600">
            A glimpse at how a real lease extension looks for both your team and your client.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {/* Mockup 1 — Case dashboard */}
          <div className="card-soft p-5 overflow-hidden" data-testid="screenshot-dashboard">
            <div className="rounded-lg bg-white border border-navy-100 overflow-hidden">
              <div className="px-4 py-3 border-b border-navy-100 flex items-center justify-between bg-navy-50">
                <p className="text-xs font-mono text-navy-500">Cases · Active</p>
                <span className="text-[10px] font-mono text-emerald-600">14 open</span>
              </div>
              <div className="p-3 space-y-2">
                {[
                  { ref: "LE-0042", addr: "14 Acacia Avenue, Flat 3", stage: "Valuation", colour: "emerald" },
                  { ref: "LE-0041", addr: "8 Lansdowne House", stage: "Counter-notice", colour: "amber" },
                  { ref: "LE-0039", addr: "Block of 7 · Greenwich", stage: "Section 13", colour: "navy" },
                  { ref: "LE-0037", addr: "22 Mile End Road", stage: "Completion", colour: "emerald" },
                ].map((c) => (
                  <div key={c.ref} className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-navy-50">
                    <span className="font-mono text-[10px] text-navy-400">{c.ref}</span>
                    <span className="text-[12px] text-navy-800 flex-1 truncate">{c.addr}</span>
                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${stagePillClass(c.colour)}`}>{c.stage}</span>
                  </div>
                ))}
              </div>
            </div>
            <p className="mt-4 font-display text-base text-navy-900">Case dashboard</p>
            <p className="mt-1 text-sm text-navy-500">Every active matter at a glance — stage, address, last update.</p>
          </div>

          {/* Mockup 2 — Timeline */}
          <div className="card-soft p-5 overflow-hidden" data-testid="screenshot-timeline">
            <div className="rounded-lg bg-white border border-navy-100 overflow-hidden">
              <div className="px-4 py-3 border-b border-navy-100 bg-navy-50">
                <p className="text-xs font-mono text-navy-500">Timeline · LE-0042</p>
              </div>
              <div className="p-4 relative">
                <div className="absolute left-[19px] top-7 bottom-4 w-0.5 bg-navy-100" />
                {[
                  { l: "Initial notice", d: "12 Sep", done: true },
                  { l: "Counter-notice", d: "08 Nov", done: true },
                  { l: "Valuation agreed", d: "21 Jan", current: true },
                  { l: "Draft transfer", d: "Mid Feb", done: false },
                  { l: "Completion", d: "Mar 2025", done: false },
                ].map((s) => (
                  <div key={s.l} className="relative flex items-center gap-3 py-1.5">
                    <span className={`relative z-10 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${stepDotClass(s)}`}>
                      {s.done && !s.current && <Check className="w-2.5 h-2.5 text-emerald-600" strokeWidth={3} />}
                      {s.current && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </span>
                    <span className={`text-[12px] flex-1 ${s.done || s.current ? "text-navy-800" : "text-navy-400"}`}>{s.l}</span>
                    <span className="text-[10px] font-mono text-navy-400">{s.d}</span>
                  </div>
                ))}
              </div>
            </div>
            <p className="mt-4 font-display text-base text-navy-900">Visual timeline</p>
            <p className="mt-1 text-sm text-navy-500">Statutory deadlines plotted automatically — the same view your client sees.</p>
          </div>

          {/* Mockup 3 — Client view / documents */}
          <div className="card-soft p-5 overflow-hidden" data-testid="screenshot-client">
            <div className="rounded-lg bg-white border border-navy-100 overflow-hidden">
              <div className="px-4 py-3 border-b border-navy-100 bg-navy-50 flex items-center justify-between">
                <p className="text-xs font-mono text-navy-500">Client view</p>
                <span className="text-[10px] font-mono text-emerald-600">Jane B.</span>
              </div>
              <div className="p-4">
                <p className="text-[11px] font-mono uppercase tracking-widest text-emerald-600">Where we are</p>
                <p className="mt-1 font-display text-sm text-navy-900">Valuation agreed</p>
                <p className="text-[12px] text-navy-500 mt-0.5">We've reached agreement on £14,200 premium.</p>
                <div className="mt-4 pt-4 border-t border-navy-100">
                  <p className="text-[11px] font-mono uppercase tracking-widest text-navy-500">Documents shared</p>
                  <div className="mt-2 space-y-1.5">
                    {["Valuation report.pdf", "Counter-notice.pdf", "Title plan.pdf"].map((f) => (
                      <div key={f} className="flex items-center gap-2 text-[12px] text-navy-700">
                        <span className="w-1 h-1 rounded-full bg-emerald-500" />
                        {f}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <p className="mt-4 font-display text-base text-navy-900">Client portal</p>
            <p className="mt-1 text-sm text-navy-500">Your client sees status, next step, and shared docs — without an email.</p>
          </div>
        </div>

        <p className="mt-10 text-center text-sm text-navy-500">
          👉 Less time on updates · 👉 Fewer emails · 👉 Happier clients · 👉 A more professional firm image
        </p>
      </div>
    </section>
  );
}
